import { useEffect, useMemo, useState } from "react";

import { Link } from "react-router-dom";

import { type SidebarAnchor, type SidebarLink, type SidebarNode } from "../../types/content.ts";
import { useLocale } from "../lib/i18n.jsx";
import { methodClass } from "../lib/methods.js";
import Icon from "../theme/Icon.jsx";

// Sidebar burra de propósito: recebe `groups` já montados e não sabe se está
// listando guias ou endpoints. Quem monta é cada rota, que é quem tem o
// contexto — a de guias sabe de frontmatter, a de API sabe do spec.
//
// Um nó tem páginas, grupos dentro, ou as duas coisas:
//   { id, label, items: [{ key, label, to, active, children }], groups: [ …nós… ] }
// children: [{ id, label, method?, path?, active }] — o índice da página ativa.

// Ids do caminho da raiz até o nó que contém a página ativa. É o que decide o
// que já vem aberto: um leitor que chega por link direto tem de ver onde está,
// sem abrir nada à mão.
function activePath(nodes: SidebarNode[], trail: string[] = []): string[] | null {
  for (const node of nodes) {
    const here = [...trail, node.id];
    if (node.items?.some((i) => i.active)) return here;
    const found = activePath(node.groups || [], here);
    if (found) return found;
  }
  return null;
}

// Filtro por texto: poda a árvore mantendo as páginas que casam e os nós que
// sobraram com algo dentro.
function filterTree(nodes: SidebarNode[], query: string): SidebarNode[] {
  if (!query) return nodes;
  return nodes
    .map((node): SidebarNode | null => {
      const items = (node.items || []).filter((i) => i.label.toLowerCase().includes(query));
      const groups = filterTree(node.groups || [], query);
      return items.length || groups.length ? { ...node, items, groups } : null;
    })
    .filter((n): n is SidebarNode => n !== null);
}

interface SidebarProps {
  groups: SidebarNode[];
  open?: boolean;
  onNavigate?: () => void;
  onAnchor?: (id: string) => void;
}

export default function Sidebar({ groups, open, onNavigate, onAnchor }: SidebarProps) {
  const { t, label } = useLocale();
  const [query, setQuery] = useState("");

  const filter = query.trim().toLowerCase();
  const tree = useMemo(() => filterTree(groups, filter), [groups, filter]);

  // Caminho até a página aberta, como conjunto: decide o padrão dos grupos
  // aninhados.
  const path = useMemo(() => new Set(activePath(groups) || []), [groups]);
  const pathKey = [...path].join("/");

  // `overrides` guarda só o que o leitor clicou. O resto segue o padrão, então
  // trocar de página não carrega decisões velhas de um ramo para outro.
  // id do grupo → aberto?, quando o leitor contraria o padrão.
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});

  // Ao navegar, o ramo da página aberta volta ao padrão (aberto). Sem isto, um
  // grupo que o leitor fechou continuaria fechado escondendo a página em que ele
  // acabou de entrar por um link do texto ou pela busca.
  useEffect(() => {
    setOverrides((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const id of path)
        if (id in next) {
          delete next[id];
          changed = true;
        }
      return changed ? next : prev;
    });
  }, [pathKey]);

  // Grupo de topo abre por padrão: recolher tudo de saída esconderia a navegação
  // inteira atrás de setas. Aninhado só abre se contém a página atual.
  // `collapsed` na config força um dos dois.
  const defaultOpen = (node: SidebarNode, depth: number): boolean => {
    if (node.collapsed === false) return true;
    if (node.collapsed === true) return false;
    return depth === 0 ? true : path.has(node.id);
  };

  // Buscando, tudo abre: um resultado escondido atrás de uma seta fechada é o
  // mesmo que nenhum resultado.
  const isOpen = (node: SidebarNode, depth: number): boolean =>
    !!filter || (overrides[node.id] ?? defaultOpen(node, depth));

  const toggle = (node: SidebarNode, depth: number) =>
    setOverrides((prev) => ({ ...prev, [node.id]: !isOpen(node, depth) }));

  const renderItems = (items: SidebarLink[]) =>
    items.map((item) => (
      <div key={item.key}>
        <Link to={item.to} className={`docs-nav-item ${item.active ? "active" : ""}`} onClick={onNavigate}>
          {item.label}
        </Link>

        {item.active && (item.children?.length ?? 0) > 0 && (
          <ul className="docs-nav-toc">
            {(item.children ?? []).map((child: SidebarAnchor) =>
              // Cabeçalho de recurso (Hotel, Flight…): rótulo, não link.
              child.kind === "group" ? (
                <li key={child.id} className="docs-nav-toc-group">
                  {child.label}
                </li>
              ) : (
                <li key={child.id}>
                  <button
                    type="button"
                    className={`docs-nav-toc-link ${child.active ? "active" : ""}`}
                    // O rótulo visível é o summary; o path completo fica no
                    // tooltip, para quem procura pela URL.
                    title={child.path ? `${child.method} ${child.path}` : undefined}
                    onClick={() => onAnchor?.(child.id)}
                  >
                    {child.method ? (
                      <>
                        <span className={`ref-method-mini ${methodClass(child.method)}`}>{child.method}</span>
                        <span className="docs-nav-toc-text">{child.label}</span>
                      </>
                    ) : (
                      child.label
                    )}
                  </button>
                </li>
              )
            )}
          </ul>
        )}
      </div>
    ));

  // Páginas primeiro, grupos aninhados depois: dentro de "Integrações", as quatro
  // páginas dela vêm antes do "Motor V1" recolhido.
  //
  // O grupo aninhado entra sem invólucro, no mesmo nível das páginas irmãs. Com
  // um wrapper recuado aqui, o recuo começava DEPOIS do último item e o grupo
  // parecia filho dele — do último item da lista, não de "Integrações".
  const renderBody = (node: SidebarNode, depth: number) => (
    <>
      {node.items.length > 0 && renderItems(node.items)}
      {node.groups?.map((child) => renderNode(child, depth + 1))}
    </>
  );

  // Todo grupo é recolhível, em qualquer profundidade: um rótulo que não recolhe
  // ao lado de um que recolhe é uma distinção que o leitor não tem como adivinhar.
  // `depth` só muda o padrão de aberto/fechado e o recuo.
  const renderNode = (node: SidebarNode, depth: number) => {
    const opened = isOpen(node, depth);
    // Contém a página aberta. Precisa aparecer no rótulo porque com o grupo
    // fechado não há item ativo visível, e o leitor perde de vista onde está.
    const inPath = path.has(node.id);
    return (
      <div key={node.id} className={`docs-nav-group depth-${depth}`}>
        <button
          type="button"
          className={`docs-nav-toggle ${opened ? "open" : ""} ${inPath ? "in-path" : ""}`}
          onClick={() => toggle(node, depth)}
          aria-expanded={opened}
        >
          <Icon name="chevron" size={12} />
          <span>{label(node.label)}</span>
        </button>
        {opened &&
          (depth === 0 ? renderBody(node, depth) : <div className="docs-nav-children">{renderBody(node, depth)}</div>)}
      </div>
    );
  };

  return (
    <aside className={`docs-sidebar ${open ? "open" : ""}`}>
      <div className="docs-search docs-search-sm">
        <Icon name="search" size={16} />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("searchPlaceholder")}
          aria-label={t("search")}
        />
      </div>

      <nav className="docs-nav">
        {tree.map((node) => renderNode(node, 0))}
        {!tree.length && <div className="docs-nav-empty mono">{t("nothingFound")}</div>}
      </nav>
    </aside>
  );
}
